// Pure helpers that turn cycle report payloads into readable view data. They accept
// both the older and newer payload keys and never hand internal codes to the UI
// without a readable fallback.

const isObject = (v) => !!v && typeof v === 'object' && !Array.isArray(v)
const hasItems = (v) => Array.isArray(v) && v.length > 0
const present = (v) => v !== null && v !== undefined && v !== ''

/** `eosinophils_pct` → `Eosinophils pct` — last-resort label for a code without a translation. */
export const humanizeCode = (code) => {
  if (!present(code)) return ''
  const text = String(code).replace(/[_:]+/g, ' ').replace(/\s+/g, ' ').trim()
  return text.charAt(0).toUpperCase() + text.slice(1)
}

const LOCALES = { de: 'de-CH', 'pt-BR': 'pt-BR', pt: 'pt-BR' }

/** A number in the viewer's locale (at most one decimal); non-numbers pass through. */
export const fmtNum = (value, language = 'en', maxDigits = 1) => {
  if (!present(value)) return ''
  const n = typeof value === 'number' ? value : Number(value)
  if (!Number.isFinite(n)) return String(value)
  return n.toLocaleString(LOCALES[language] ?? 'en-US', { maximumFractionDigits: maxDigits })
}

/** `value unit` with a locale number; empty string when there is no value. */
export const valueWithUnit = (value, unit, language) => {
  const n = fmtNum(value, language, 2)
  return n ? [n, unit].filter(present).join(' ') : ''
}

/** marker code → display name, gathered from every place a report names a marker. */
export const markerNames = (sections = [], extra = []) => {
  const names = {}
  const add = (code, name) => { if (present(code) && present(name) && !names[code]) names[code] = String(name) }
  for (const s of sections) {
    const f = isObject(s?.facts) ? s.facts : {}
    ;(f.markers ?? []).forEach((m) => add(m?.marker_code, m?.marker_name))
    ;(f.real_findings ?? []).forEach((m) => add(m?.marker_code, m?.marker_name))
    ;(f.tests ?? []).forEach((x) => add(x?.marker_code, String(x?.marker_name ?? '') || null))
    ;(f.answers ?? []).forEach((a) => (a?.markers ?? []).forEach((m) => add(m?.marker_code, m?.marker_name)))
  }
  extra.forEach((m) => add(m?.marker_code ?? m?.code, m?.marker_name ?? m?.name))
  return names
}

export const markerLabel = (names, code, name) => (present(name) ? String(name) : names?.[code] ?? humanizeCode(code))

/** Does a section carry structured facts the tab renders as a list? */
export const sectionHasContent = (section, recommendationCount = 0) => {
  const f = isObject(section?.facts) ? section.facts : {}
  switch (section?.id) {
    case 'verdict': return (f.results_count ?? 0) > 0 || hasItems(f.real_findings)
    case 'questions': return hasItems(f.answers)
    case 'markers_by_cause': return hasItems(f.markers)
    case 'system_view': return hasItems(f.loops) || isObject(f.readiness)
    case 'improve_training': return hasItems(f.kept) || hasItems(f.change)
    case 'improve_supplements': return !!f.none_recorded || hasItems(f.groups)
    case 'improve_wellness': return wellnessLines(f).length > 0 || !!f.sleep_constancy?.applies || present(f.recovery_status)
    case 'nutrition_findings': return hasItems(f.lines)
    case 'levers': return recommendationCount > 0
    case 'conflicts': return hasItems(f.conflicts)
    case 'preparation': return hasItems(f.tests) || hasItems(f.protocol)
    case 'strengths': return hasItems(f.stable_good_markers) || hasItems(f.improved_markers) || hasItems(f.progressing_exercises)
    default: return false
  }
}

const NAME_KEYS = ['marker_name', 'exercise_name', 'name', 'title', 'question', 'test']

const collectNames = (value, out = new Set()) => {
  if (Array.isArray(value)) value.forEach((v) => collectNames(v, out))
  else if (isObject(value)) {
    NAME_KEYS.forEach((k) => { if (typeof value[k] === 'string' && value[k].trim().length > 2) out.add(value[k].trim().toLowerCase()) })
    Object.values(value).forEach((v) => { if (typeof v === 'object') collectNames(v, out) })
  }
  return out
}

/**
 * The prose is shown only when it adds something to the structured list: a section
 * without structured content keeps its narrative; template text restating the facts is
 * dropped; AI prose is dropped when it merely walks through the listed names.
 */
export const showNarrative = (section, hasContent) => {
  const text = typeof section?.narrative === 'string' ? section.narrative.trim() : ''
  if (!text) return false
  if (!hasContent) return true
  if (section.narrative_source !== 'ai') return false
  const names = [...collectNames(section.facts)]
  if (names.length < 2) return true
  const lower = text.toLowerCase()
  const mentioned = names.filter((n) => lower.includes(n)).length
  return mentioned / names.length < 0.5
}

/** A supplement with something to say: an action other than keep, a limit, a marker fit or a risk. */
const hasFinding = (s, group) => {
  const rec = s?.recommendation ?? group
  return (present(rec) && rec !== 'keep')
    || hasItems(s?.above_limit) || hasItems(s?.fit_with_markers) || present(s?.organ_risk)
    || (present(s?.evidence) && s.evidence !== 'not_classified')
}

export const supplementsView = (facts) => {
  const flagged = []
  const quiet = []
  for (const group of Array.isArray(facts?.groups) ? facts.groups : []) {
    const items = (group?.supplements ?? []).filter(isObject)
    const withFinding = items.filter((s) => hasFinding(s, group.recommendation))
    if (withFinding.length) flagged.push({ recommendation: group.recommendation, supplements: withFinding })
    items.filter((s) => !hasFinding(s, group.recommendation)).forEach((s) => quiet.push(s.name))
  }
  return { flagged, quiet: quiet.filter(present) }
}

export const WELLNESS_METRICS = ['sleep_minutes', 'resting_hr', 'steps']

/** One line per wellness metric that has a value; a metric with only months is left out. */
export const wellnessLines = (facts) => WELLNESS_METRICS.map((key) => {
  const m = facts?.[key]
  if (!isObject(m)) return null
  const first = m.first_value ?? m.first
  const last = m.last_value ?? m.last
  if (!present(m.mean) && !(present(first) && present(last))) return null
  return {
    key, unit: m.unit ?? '', mean: m.mean ?? null, change: m.change ?? null,
    first: present(first) ? first : null, last: present(last) ? last : null,
    from: m.first_month ?? null, to: m.last_month ?? null,
  }
}).filter(Boolean)

/**
 * Parameters each target reason sentence needs. A reason whose parameters are not all
 * present (or whose code is unknown) falls back to the short reason label — never to a
 * half-filled sentence.
 */
export const REASON_PARAMS = {
  calories_measured_expenditure: ['maintenance_kcal', 'adjustment_kcal', 'goal_weight_kg'],
  protein_reduced_kidney: ['g_per_kg', 'weight_kg', 'egfr'],
  protein_per_kg_training: ['g_per_kg', 'weight_kg'],
  fat_per_kg: ['g_per_kg', 'weight_kg'],
  carbs_remaining_energy: ['kcal', 'protein_g', 'fat_g'],
  training_day_extra: ['rest_day_g', 'extra_g'],
  fiber_per_1000_kcal: ['g_per_1000_kcal', 'minimum_g'],
  goal_weight: ['weight_kg'],
  sweet_baked_share: ['current_share_pct', 'max_share_pct'],
  egg_cap: ['current_per_day'],
  egg_cap_ldl: ['current_per_day', 'ldl'],
  alcohol_halve: ['current_ml_per_week'],
}

/** `{key, params}` for the reason sentence of a target, or null. */
export const reasonSentence = (target, language) => {
  const code = target?.reason_code
  const required = REASON_PARAMS[code]
  const raw = isObject(target?.reason_params) ? target.reason_params : null
  if (!required || !raw || !required.every((k) => present(raw[k]))) return null
  const params = Object.fromEntries(Object.entries(raw).map(([k, v]) => [k, typeof v === 'number' ? fmtNum(v, language) : v]))
  let key = code
  if (code === 'calories_measured_expenditure') {
    const adj = Number(raw.adjustment_kcal)
    params.adjustment_kcal = fmtNum(Math.abs(adj), language)
    key = adj < 0 ? `${code}_lose` : adj > 0 ? `${code}_gain` : `${code}_maintain`
  }
  return { key, params }
}

/** Sleep/recovery experiment parameters, only when complete. */
export const sleepExperiment = (constancy) => {
  const e = constancy?.experiment
  if (!isObject(e) || ![e.duration_days, e.baseline_minutes, e.suggested_target_minutes].every(present)) return null
  return { days: e.duration_days, baseline: e.baseline_minutes, target: e.suggested_target_minutes }
}

/** Daily supplement dose from the per-time quantities, else the default quantity; never the container size. */
export const dailyDose = (s) => {
  const parts = [s?.morning_quantity, s?.midday_quantity, s?.evening_quantity].map(Number).filter((n) => Number.isFinite(n) && n > 0)
  const quantity = parts.length ? parts.reduce((a, b) => a + b, 0) : Number(s?.default_quantity) > 0 ? Number(s.default_quantity) : null
  return quantity == null ? null : { quantity, unit: s?.quantity_unit ?? null }
}
